import { zodResolver } from '@hookform/resolvers/zod';
import {
  DateInput,
  Form,
  FormButton,
  Input,
  number,
  SelectInput,
  string,
  SwitchInput,
  useNotifications
} from '@ijoin/components';
import {
  getGetAdminPlanManagedAccountProgramQueryKey,
  useGetAdminAdvisoryOrganizationManagedAccountProvidersSuspense,
  useGetAdminAdvisoryOrganizationMethodologiesSuspense,
  useGetAdminPlanManagedAccountProgramSuspense,
  usePatchAdminManagedAccountProgram
} from '@ijoin/hooks';
import { Divider, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { orderBy } from 'lodash';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Field } from './Field';

type ProgramFormProps = {
  automaticReenrollment: boolean;
  enrollmentSiteGoLiveDate: null | string;
  enrollNoticeRequiredAcknowledgement: boolean;
  hasAutoReenrollForAllParticipants: boolean;
  managedAccountProviderId: string;
  optOutDays: number;
  optOutDeadline: null | string;
  programStatus: string;
  shareClass: string;
  startDate: null | string;
  tiaaSiaFundCusip: string;
};

const PROGRAM_STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Archive', value: 'archive' }
];

const resolver = zodResolver(
  z.object({
    automaticReenrollment: z.boolean(),
    enrollmentSiteGoLiveDate: z.string().nullable(),
    enrollNoticeRequiredAcknowledgement: z.boolean(),
    hasAutoReenrollForAllParticipants: z.boolean(),
    managedAccountProviderId: string(),
    optOutDays: number({ min: 0 }),
    optOutDeadline: z.string().nullable(),
    programStatus: string(),
    shareClass: string({ maxLength: 10 }),
    startDate: z.string().nullable(),
    tiaaSiaFundCusip: string({ maxLength: 9 })
  })
);

export const PlanManagedAccountProgramTab: FC<{
  advisoryOrganizationId: string;
  planId: string;
}> = props => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const program = useGetAdminPlanManagedAccountProgramSuspense(props.planId);
  const providers =
    useGetAdminAdvisoryOrganizationManagedAccountProvidersSuspense(
      props.advisoryOrganizationId,
      { query: { select: data => orderBy(data, 'description', 'asc') } }
    );
  const methodologies = useGetAdminAdvisoryOrganizationMethodologiesSuspense(
    props.advisoryOrganizationId
  );

  const methods = useForm<ProgramFormProps>({
    mode: 'all',
    resolver,
    values: {
      automaticReenrollment: program.data?.automaticReenrollment ?? false,
      enrollmentSiteGoLiveDate: program.data?.enrollmentSiteGoLiveDate ?? null,
      enrollNoticeRequiredAcknowledgement:
        program.data?.enrollNoticeRequiredAcknowledgement ?? false,
      hasAutoReenrollForAllParticipants:
        program.data?.hasAutoReenrollForAllParticipants ?? false,
      managedAccountProviderId: program.data?.managedAccountProviderId ?? '',
      optOutDays: program.data?.optOutDays ?? 0,
      optOutDeadline: program.data?.optOutDeadline ?? null,
      programStatus: program.data?.programStatus ?? '',
      shareClass: program.data?.shareClass ?? '',
      startDate: program.data?.startDate ?? null,
      tiaaSiaFundCusip: program.data?.tiaaSiaFundCusip ?? ''
    }
  });

  const updateProgram = usePatchAdminManagedAccountProgram({
    mutation: {
      onError: () =>
        notifications.error('Failed to update managed account program'),
      onSuccess: () => {
        notifications.success('Managed account program updated successfully');
        queryClient.invalidateQueries({
          queryKey: getGetAdminPlanManagedAccountProgramQueryKey(props.planId)
        });
      }
    }
  });

  const selectedProviderId = methods.watch('managedAccountProviderId');
  const selectedProvider = providers.data?.find(
    p => p.managedAccountProviderId === selectedProviderId
  );
  const selectedMethodologyName = methodologies.data?.find(
    m =>
      m.managedAccountMethodologyId ===
      selectedProvider?.managedAccountMethodologyId
  )?.managedAccountMethodologyName;

  const onSubmit = (data: ProgramFormProps) => {
    if (!program.data) return;

    updateProgram.mutate({
      data: {
        automaticReenrollment: data.automaticReenrollment,
        enrollmentSiteGoLiveDate: data.enrollmentSiteGoLiveDate ?? undefined,
        enrollNoticeRequiredAcknowledgement:
          data.enrollNoticeRequiredAcknowledgement,
        hasAutoReenrollForAllParticipants:
          data.hasAutoReenrollForAllParticipants,
        managedAccountProviderId: data.managedAccountProviderId || null,
        optOutDays: data.optOutDays,
        optOutDeadline: data.optOutDeadline ?? undefined,
        programStatus: data.programStatus || undefined,
        shareClass: data.shareClass || undefined,
        startDate: data.startDate ?? undefined,
        tiaaSiaFundCusip: data.tiaaSiaFundCusip || undefined
      },
      managedAccountProgramId: program.data.managedAccountProgramId
    });
  };

  if (!program.data) {
    return (
      <Typography color='text.secondary' variant='body2'>
        This plan has no managed account program.
      </Typography>
    );
  }

  return (
    <Form
      data-testid='planManagedAccountProgramForm'
      methods={methods}
      noValidate
      onSubmit={methods.handleSubmit(onSubmit)}>
      <Stack
        pt={2}
        spacing={2}
        sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Typography color='text.secondary' variant='h6'>
          Program Info
        </Typography>
        <Divider />
        <Stack
          sx={{
            columnGap: 2,
            display: 'grid',
            gridTemplateColumns: { sm: 'repeat(2, 1fr)', xs: '1fr' },
            rowGap: 2
          }}>
          <SelectInput
            data-testid='programStatus'
            fullWidth
            hideError
            label='Program Status'
            name='programStatus'
            options={PROGRAM_STATUS_OPTIONS}
            size='small'
            variant='outlined'
          />
          <SelectInput
            data-testid='managedAccountProviderId'
            fullWidth
            hideError
            label='Provider'
            name='managedAccountProviderId'
            options={[
              { label: '(None)', value: '' },
              ...(providers.data ?? []).map(p => ({
                label: p.description,
                value: p.managedAccountProviderId
              }))
            ]}
            size='small'
            variant='outlined'
          />
          <Field label='Methodology' value={selectedMethodologyName ?? ''} />
          <Input
            data-testid='shareClass'
            format='string'
            fullWidth
            hideError
            label='Share Class'
            name='shareClass'
            size='small'
          />
          <Input
            data-testid='tiaaSiaFundCusip'
            format='string'
            fullWidth
            hideError
            label='TIAA SIA Fund CUSIP'
            name='tiaaSiaFundCusip'
            size='small'
          />
        </Stack>

        <Typography color='text.secondary' variant='h6'>
          Dates
        </Typography>
        <Divider />
        <Stack
          sx={{
            columnGap: 2,
            display: 'grid',
            gridTemplateColumns: { sm: 'repeat(2, 1fr)', xs: '1fr' },
            rowGap: 2
          }}>
          <DateInput
            data-testid='startDate'
            fullWidth
            hideError
            label='Start Date'
            name='startDate'
            size='small'
          />
          <DateInput
            data-testid='optOutDeadline'
            fullWidth
            hideError
            label='Opt-Out Deadline'
            name='optOutDeadline'
            size='small'
          />
          <DateInput
            data-testid='enrollmentSiteGoLiveDate'
            fullWidth
            hideError
            label='Enrollment Site Go-Live Date'
            name='enrollmentSiteGoLiveDate'
            size='small'
          />
          <Input
            data-testid='optOutDays'
            format='number'
            fullWidth
            hideError
            label='Opt-Out Days'
            min={0}
            name='optOutDays'
            required
            size='small'
          />
        </Stack>

        <Typography color='text.secondary' variant='h6'>
          Enrollment Settings
        </Typography>
        <Divider />
        <Stack
          sx={{
            columnGap: 2,
            display: 'grid',
            gridTemplateColumns: { sm: 'repeat(2, 1fr)', xs: '1fr' },
            rowGap: 2
          }}>
          <SwitchInput
            color='success'
            data-testid='automaticReenrollment'
            hideError
            label='Automatic Re-enrollment'
            name='automaticReenrollment'
          />
          <SwitchInput
            color='success'
            data-testid='hasAutoReenrollForAllParticipants'
            hideError
            label='Auto Re-enroll All Participants'
            name='hasAutoReenrollForAllParticipants'
          />
          <SwitchInput
            color='success'
            data-testid='enrollNoticeRequiredAcknowledgement'
            hideError
            label='Enroll Notice Acknowledgement Required'
            name='enrollNoticeRequiredAcknowledgement'
          />
        </Stack>

        <FormButton
          data-testid='savePlanManagedAccountProgram'
          sx={{ alignSelf: 'flex-start' }}
          triggerValidation
          variant='contained'>
          Save
        </FormButton>
      </Stack>
    </Form>
  );
};

PlanManagedAccountProgramTab.displayName = 'PlanManagedAccountProgramTab';
